import React from 'react'
import PropTypes from 'prop-types'
import {
  PopupAddress,
  AuctionInfo,
  PropertyInfo,
  PropertyValue,
  linkPropType,
  CardImage,
} from './cardParts'
import {
  GRAPH_API_FRAGMENT_DEFINITION,
  GRAPH_API_FRAGMENT_REFERENCE,
} from '../../../utils/symbols'
import { formatListingImgUrl } from '../utils/formatters'

import classnames from 'classnames/bind'
import cls from './styles.scss'
import { getCurrentBid } from '../listingCard/cardHelpers'
import { getListingImageUrl } from '../../../utils/listing'
import { useBackgroundImageWithFallback } from '../../../hooks/useBackgroundImageWithFallback'

const cx = classnames.bind(cls)

const LISTING_CARD_PHOTO_WIDTH = 110

const getListingPhotoUrl = (listingPrimaryPhoto, multiplier = 1) =>
  formatListingImgUrl(listingPrimaryPhoto, {
    width: LISTING_CARD_PHOTO_WIDTH * multiplier,
  })

const ListingPopupCard = ({
  listing,
  elmId,
  className,
  linkProps,
  onMouseOver,
  onMouseOut,
  isCancelled = false,
  featureFlags = {},
}) => {
  const onMouseClick = () => {
    !isCancelled &&
      linkProps.onClick &&
      linkProps.onClick(getAnalyticsAttributes(listing))
  }

  const getAnalyticsAttributes = listing => ({
    displayedCurrentBid: getCurrentBid(listing),
  })

  const primaryPhoto = useBackgroundImageWithFallback(
    listing.primary_photo,
    featureFlags['fe.resi.enabled-photo-enhancement']
  )

  const listingPhotoUrl = getListingPhotoUrl(primaryPhoto, 2)

  return (
    <div
      className={cx(
        'popup-card',
        { cancelled: isCancelled },
        {
          seen: listing.primary_property?.current_user_tracking_state?.is_seen,
        },
        className
      )}
      onClick={onMouseClick}
      onMouseOver={onMouseOver}
      onMouseLeave={onMouseOut}
      data-elm-id={elmId}
    >
      <CardImage
        listing={listing}
        listingPhotoUrl={listingPhotoUrl}
        defaultPhotoUrl={getListingImageUrl()}
      />
      <div className={cx('info-section')}>
        <PropertyValue
          listing={listing}
          elmId={elmId}
          isPopup
          featureFlags={featureFlags}
        />
        {!isCancelled && (
          <PopupAddress
            address={listing.formatted_address_line_1[0]}
            elmId={elmId}
          />
        )}
        <PropertyInfo
          listing={listing}
          elmId={elmId}
          className={cx('popup-property-info')}
          isCancelled={isCancelled}
        />
        <AuctionInfo
          listing={listing}
          elmId={elmId}
          className={cx('popup-auction-info')}
          hideAuctionDate
        />
      </div>
    </div>
  )
}

ListingPopupCard.propTypes = {
  listing: PropTypes.object,
  elmId: PropTypes.string,
  className: PropTypes.string,
  linkProps: linkPropType,
  onMouseOver: PropTypes.func,
  onMouseOut: PropTypes.func,
  isCancelled: PropTypes.bool,
  featureFlags: PropTypes.object,
}

// alias for formatted address to allow us to merge the fragments for listing popup card and listing card without collision
ListingPopupCard[GRAPH_API_FRAGMENT_DEFINITION] = `
  fragment ListingPopupCardFields on Listing {
    listing_id
    formatted_address_line_1: formatted_address(format: SINGLE_LINE)
    listing_summary {
      is_remote_bid_enabled
      is_remote_before_and_during_auction_enabled
      show_opening_bid
    }
    listing_status_group
    listing_page_path
    primary_photo
    reserve_price

    listing_configuration {
      product_type
      asset_type
      is_third_party_online
    }

    event {
      event_code
    }

    seller_property {
      street_description
      country_secondary_subdivision
      municipality
      postal_code
    }

    program_configuration {
      program_enrollment_code
    }

    primary_property {
      property_id
      summary {
        total_bedrooms
        total_bathrooms
        square_footage
        address {
          coordinates {
            lat
            lon
          }
        }
      }
      is_newly_listed
      current_user_tracking_state {
        is_seen
        is_updated
      }
    }

    auction {
      starting_bid
      is_online
      bid_instruction {
        nos_amount
      }
    }

    online_auction_segment(resolvePolicy: CACHE_ONLY) {
      __typename
      current_highest_bid {
        bid_amount
      }
    }

    live_auction_segment(resolvePolicy: CACHE_ONLY) {
      current_highest_bid {
        bid_amount
      }
    }
  }
`

ListingPopupCard[GRAPH_API_FRAGMENT_REFERENCE] = `...ListingPopupCardFields`

export default ListingPopupCard
